import React, { useEffect, useState } from 'react';
import { Card, Container, Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../middleware/ThemeContext';
import { CorporateTheme, Theme } from '../../utils/themes';
import ArrowIcon from '../../assets/icons/ArrowIcon';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import StyledText from '../../components/StyledComponents/StyledText';
import { SkeletonCell } from '../../components/SkeletonCell';
import { authFetch } from '../../utils/authFetch';

// TypeScript Interfaces
interface ContactSummaryData {
    fullName: string;
    emailAddress: string;
    primaryOrganization: string;
    contactRole: string;
}

interface OrganizationSummaryData {
    organizationName: string;
    organizationType: string;
    userRole: string;
    contactRole: string;
}

type SummaryType = 'contact_summary' | 'contact_type' | 'organization_summary';

interface SummaryCardProps {
    summaryType: SummaryType;
    contactData?: ContactSummaryData;
    organizationData?: OrganizationSummaryData;
    onProfileClick?: () => void;
    onManageOrganizationClick?: () => void;
}

// Styled Components
const SummaryCard = styled(Card) <{ theme: Theme }>`
  border: 1px solid ${({ theme }) => CorporateTheme.colors.border};
  border-radius: 8px;
  max-width: 600px;
  margin: 20px auto;
  background-color: ${({ theme }) => CorporateTheme.colors.background};
    padding: 16px 24px;

`;

const CardHeader = styled.div`
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 15px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
`;

const CardBody = styled.div`
margin-top:20px;
`;

const FieldRow = styled(Row)`
  margin-bottom: 16px;
  
  &:last-of-type {
    margin-bottom: 24px;
  }
`;

const FieldLabel = styled(Col)`
  font-weight: 500;
  color: #4b5563;
  font-size: 14px;
  padding-right: 8px;
  text-align: right;
  
  @media (max-width: 576px) {
    text-align: left;
    margin-bottom: 4px;
  }
`;

const FieldValue = styled(Col)`
  color: #374151;
  font-size: 14px;
  padding-left: 8px;
`;

const ActionButton = styled.button <{ theme: Theme }>`
  width: 100%;
  padding: 12px 24px;
  background-color: ${({ theme }) => CorporateTheme.colors.headerBG};
  border:none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
 
`;



// Main Component
const SummaryCardComponent: React.FC<SummaryCardProps> = ({
    summaryType,
    contactData,
    organizationData,
    onProfileClick,
    onManageOrganizationClick,
}) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [data, setData] = useState<any | null>(null);
    
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;

        const fetchSummaryDetails = async () => {
            try {
                setIsFetching(true);
                if (!data) {
                    setIsLoading(true);
                }

                const apiBase = process.env.REACT_APP_API_URL;
                if (!apiBase) {
                    console.warn('REACT_APP_API_URL is not configured; summary details cannot be loaded.');
                    return;
                }

                const response = await authFetch(`${apiBase}/portal/me`);
                const json: any = await response.json();

                if (!cancelled) {
                    setData(json);
                    // eslint-disable-next-line no-console
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load summary details', error);
                    setData(null);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsFetching(false);
                }
            }
        };

        void fetchSummaryDetails();

        return () => {
            cancelled = true;
        };
    }, []);

    const isContactSummary = summaryType === 'contact_summary' || summaryType === 'contact_type';

    const getTitle = () => {
        return isContactSummary ? 'Contact Summary' : 'Organization Summary';
    };

    const getButtonConfig = () => {
        if (isContactSummary) {
            return {
                text: 'Go to My Profile',
                onClick: onProfileClick,
            };
        }
        return {
            text: 'Manage Organization',
            onClick: onManageOrganizationClick,
        };
    };

    const buttonConfig = getButtonConfig();

    const handleActionClick = () => {
        const id = isContactSummary ? data?.data?.id : data?.data?.primaryOrganization?.id;
        const targetPage = isContactSummary ? 'contactDetails' : 'organizationDetails';
        const params = new URLSearchParams(window.location.search);

        params.set('page', targetPage);
        params.delete('edit');

        if (id !== undefined && id !== null && id !== '') {
            params.set('id', String(id));
        } else {
            params.delete('id');
        }

        navigate(`?${params.toString()}`);
    };

    return (
        <Container className='p-0'>
            <SummaryCard theme={theme}>
                <CardHeader>
                    <StyledText fontSize='15px' fontWeight={700} textColor={CorporateTheme.colors.headerColor}>{getTitle()}</StyledText>
                </CardHeader>
                <CardBody>
                    {isContactSummary ? (
                        <div className='d-flex flex-column gap-3'>
                            <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    Full Name:
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> : <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.fullName || ''}
                                </StyledText>}
                            </div>

                            <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    Email Address:
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> : <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.email || ''}
                                </StyledText>}
                            </div>

                            <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    Primary Organization:
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> : <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.primaryOrganization?.name || ''}
                                </StyledText>}
                            </div>

                            <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    Contact Roles:
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> : <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.allRoles?.map((role: any) => role.name).join(', ') || ''}
                                </StyledText>}
                            </div>
                        </div>
                    ) : (
                        <div className='d-flex flex-column gap-3'>
                            <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    Organization Name:
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> : <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.primaryOrganization?.name || ''}
                                </StyledText>}
                            </div>

                            {/* <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    User Role :
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> :<StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.userRole?.name || ''}
                                </StyledText>}
                            </div> */}

                            <div className='d-flex align-items-center gap-1'>
                                <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.headerColor}>
                                    Contact Roles:
                                </StyledText>
                                {isLoading || isFetching ? <SkeletonCell width={'100px'} height={'14px'} /> : <StyledText fontSize='14px' fontWeight={600} textColor={CorporateTheme.colors.textSecondary}>
                                    {data?.data?.primaryOrganizationRoles?.map((role: any) => role.name).join(', ') || ''}
                                </StyledText>}
                            </div>
                        </div>
                    )}

                    <ActionButton className="mt-4" theme={theme} onClick={handleActionClick}> 
                        <StyledText fontSize='12px' fontWeight={500} textColor={CorporateTheme.colors.headerColor}>{buttonConfig.text}</StyledText>
                        <ChevronDownIcon color={CorporateTheme.colors.headerColor} size={14} rotate='left' />
                    </ActionButton>
                </CardBody>
            </SummaryCard>
        </Container>
    );
};

export default SummaryCardComponent;